import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Div100vh from "react-div-100vh";
import Header from "../common/Header/header";
import PrimaryButton from "../common/buttons/PrimaryButton";
import { updateFunction, trackBtnClick, fetchData } from "@/lib/helper";
import CustomDrawer from "../common/drawer";
import { ChevronLeftIcon } from "lucide-react";
import { CONSENT_MESSAGES } from "@/lib/data";
import { trackConsent } from "@/lib/apis";
import { lenderConsentLabel, lenderNamePhrase, useLender } from "@/lib/lender";

export default function RentSummary() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [addressChecked, setAddressChecked] = useState(true);
  const [linkDrawer, setLinkDrawer] = useState(false);
  const [currLink, setCurrLink] = useState("");
  const [data, setData] = useState(null);
  const lender = useLender(data?.lender);

  useEffect(() => {
    const fetchJourneyData = async () => {
      try {
        const data = await fetchData("journey/full");
        setData(data.journey);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchJourneyData();
  }, []);

  const handleClick = async () => {
    if (!isChecked || !addressChecked) {
      setError("Please tick the checkbox!");
      return;
    }

    try {
      setIsLoading((prevState) => !prevState);
      await updateFunction(
        {
          acceptedRentSummary: true,
        },
        "user/update"
      );
      const platform =
        navigator?.userAgentData?.platform ||
        navigator.platform ||
        localStorage.getItem("UTM_SOURCE");
      await trackConsent(
        "Checkbox",
        "Current Address",
        "",
        platform,
        CONSENT_MESSAGES.ADDRESS
      );
      await trackConsent(
        "Checkbox",
        lenderConsentLabel(lender),
        "",
        platform,
        lender.consents.apply
      );
      await trackConsent(
        "Checkbox",
        "CKYC",
        "",
        platform,
        lender.consents.ckyc
      );
      trackBtnClick("Confirm", "Rent Summary");
      navigate("/onboarding");
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading((prevState) => !prevState);
    }
  };

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleLinkClick = (link) => {
    // setIsChecked(!isChecked);
    switch (link) {
      case "tncs":
        setCurrLink(
          "https://circleapp-docs.s3.us-east-1.amazonaws.com/PL_Rent+Financing_Application+Terms+and+Consent.docx.pdf"
        );
        break;
      case "application":
        setCurrLink(
          "https://circleapp-docs.s3.us-east-1.amazonaws.com/2.+APPLICATION+TERMS+-+PLCP+-+MOBILE+JOURNEY.docx.pdf"
        );
      default:
        break;
    }
    setLinkDrawer(true);
  };

  // JSON object to pass all the data
  let rentSummaryData;

  if (data) {
    const formattedMoveInDate = new Date(data.moveInDate).toLocaleDateString(
      "en-GB"
    );

    rentSummaryData = {
      header: "Rent Summary",
      rentDetails: [
        { title: "Your rent", value: `${data?.rent || ""}` },
        {
          title: "Lock In",
          value: `${data.loanTenure ? data.loanTenure : 10}`,
          unit: "months",
        },
        {
          title: "Lease",
          value: `${data.loanTenure ? data.loanTenure + 1 : 9}`,
          unit: "months",
        },
      ],
      securityDeposit: 0,
      propertyDetails: {
        moveInDate: formattedMoveInDate,
        address: data.property?.address || "",
      },
      landlordDetails: {
        name: `${data.landlord?.full_name || ""}`,
        phone: `${data.landlord?.phone || ""}`,
      },
    };
  }

  useEffect(() => {
    setError("");
  }, [isChecked]);

  if (data) {
    return (
      <Div100vh className="flex flex-col justify-between py-4 px-6">
        {/* Header */}
        <Header heading={rentSummaryData.header} />

        {/* Main Content */}
        <div className="flex flex-col justify-between h-full gap-8 pb-6 overflow-scroll">
          <div className="bg-black text-white rounded-lg shadow-lg max-w-md">
            {/* Rent Details */}
            <div className="flex justify-around mt-4 space-x-4">
              {rentSummaryData.rentDetails.map((detail, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center justify-center w-full gap-1 bg-[#333] text-center p-2"
                >
                  <p className="text-gray-400 text-sm">{detail.title}</p>
                  <p className="text-white text-lg font-medium">
                    {detail.value.toLocaleString()}
                  </p>
                  {detail.unit && (
                    <p className="text-gray-400 text-xs">{detail.unit}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Property Details */}
          <div className="flex flex-col text-lg">
            Property Details
            <div>
              <InfoField
                title={"Move-In date"}
                value={rentSummaryData.propertyDetails.moveInDate}
              />
              <InfoField
                title={"Address"}
                value={
                  rentSummaryData.propertyDetails.address ||
                  "Sector 27, Galleria Market, Gurugram. 122001"
                }
              />
            </div>
            {/* Checkbox to show: Use this as my correspondance address, */}
            <div
              className="flex items-center space-x-3 text-gray-400 mb-2 mt-4"
              onClick={() => setAddressChecked((prevState) => !prevState)}
            >
              {/* Checkbox */}
              <div
                className={`w-4 h-4 border-2 aspect-square mt-0.5 ${
                  addressChecked ? " bg-[#eee]" : "border-gray-400"
                } flex items-center justify-center cursor-pointer`}
              >
                {addressChecked && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-black"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
              {/* Label */}
              <label htmlFor="confirmation-checkbox" className="text-2xs">
                Use this as my correspondence address
              </label>
            </div>
          </div>

          {/* Landlord Details */}
          <div className="flex flex-col text-lg">
            Landlord Details
            <div>
              <InfoField
                title={"Name"}
                value={rentSummaryData.landlordDetails.name}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center space-x-3 text-gray-400 mb-2 mt-4">
              {/* Checkbox */}
              <div
                className={`w-4 h-4 border-2 aspect-square mt-0.5 ${
                  isChecked ? " bg-[#eee]" : "border-gray-400"
                } flex items-center justify-center cursor-pointer`}
                onClick={handleCheckboxChange}
              >
                {isChecked && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-black"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
              {/* Label */}
              <label htmlFor="confirmation-checkbox" className="text-3xs">
                I hereby apply the RENT FINANCING with {lenderNamePhrase(lender)}{" "}
                as per the details provided by me
                and I confirm to have read, understood and agreed to the{" "}
                <a
                  href={lender.productUrl}
                  target="_blank"
                  className="underline text-blue-600 cursor-pointer"
                >
                  PRODUCT DETAILS
                </a>{" "}
                and other relevant{" "}
                <span
                  className="underline text-blue-600 cursor-pointer"
                  onClick={() => handleLinkClick("application")}
                >
                  APPLICATION TERMS
                </span>
              </label>
            </div>
            <div className="flex items-center space-x-3 text-gray-400 mb-5">
              {/* Checkbox */}
              <div
                className={`w-4 h-4 border-2 aspect-square mt-0.5 ${
                  isChecked ? " bg-[#eee]" : "border-gray-400"
                } flex items-center justify-center cursor-pointer`}
                onClick={handleCheckboxChange}
              >
                {isChecked && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-black"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
              {/* Label */}
              {/* <label htmlFor="confirmation-checkbox" className="text-3xs">
                I hereby authorize and give my consent to FFPL to download my
                KYC records from the Central KYC Registry (CKYCR) for the
                purpose of verification of my identity and address from the
                CKYCR database.
              </label> */}
            </div>
          </div>
        </div>
        <CustomDrawer isOpen={linkDrawer}>
          {/* <GoBackButton customBackSubmit={()=>setLinkDrawer(false)}/> */}
          <div className="h-full w-full absolute left-0 top-0">
            <iframe
              src={currLink}
              title="Website Frame"
              className="w-full h-full rounded-xl border border-[#444] my-4"
              allowFullScreen
            ></iframe>
          </div>
          <button
            onClick={() => setLinkDrawer(false)}
            className="absolute top-8 left-8 p-2 cursor-pointer hover:bg-slate-100 se:w-8 se:h-8 sm:h-11 sm:w-11 bg-white rounded-full flex flex-col justify-center items-center"
          >
            <ChevronLeftIcon className="size-112  text-black" />
          </button>
        </CustomDrawer>
        <div className="mt-5">
          {/* Primary Button */}
          <PrimaryButton
            text={"Confirm"}
            handleClick={() => handleClick()}
            disabled={isLoading}
            loading={isLoading}
            error={error}
          />
        </div>
      </Div100vh>
    );
  } else return null;
}

function InfoField({ title, value }) {
  return (
    <div className="flex flex-col gap-2 mt-2">
      <span className="text-xs text-[#999] ml-1">{title}</span>
      <div className="flex items-center justify-start w-full border border-[#444] p-3 text-sm bg-[#171717] text-[#bbb]">
        {value}
      </div>
    </div>
  );
}
